
/**
 * Constant with coefficient for converting Degree to Radians
 *
 * @type {number}
 */
const CONST_DEG_2_RAD_COEF = Math.PI / 180;

/**
 * Possible Min value
 *
 * @type {number}
 */
const CONST_EPSILON = 0.0001;

/**
 * Multiplies two 3x3 matrices
 *
 * @param {(Array|Float32Array)} src
 * @param {(Array|Float32Array)} dst
 * @return {void}
 */
const multiply = (src, dst) => {
	let a11 = src[0], a12 = src[1], a13 = src[2],
		a21 = src[3], a22 = src[4], a23 = src[5],
		a31 = src[6], a32 = src[7], a33 = src[8],
		b11 = dst[0], b12 = dst[1], b13 = dst[2],
		b21 = dst[3], b22 = dst[4], b23 = dst[5],
		b31 = dst[6], b32 = dst[7], b33 = dst[8];

	src[0] = b11 * a11 + b12 * a21 + b13 * a31;
	src[1] = b11 * a12 + b12 * a22 + b13 * a32;
	src[2] = b11 * a13 + b12 * a23 + b13 * a33;

	src[3] = b21 * a11 + b22 * a21 + b23 * a31;
	src[4] = b21 * a12 + b22 * a22 + b23 * a32;
	src[5] = b21 * a13 + b22 * a23 + b23 * a33;

	src[6] = b31 * a11 + b32 * a21 + b33 * a31;
	src[7] = b31 * a12 + b32 * a22 + b33 * a32;
	src[8] = b31 * a13 + b32 * a23 + b33 * a33;
};

/**
 * Matrix 3x3
 */
export default class Mat3 {
	/**
	 * Class constructor
	 *
	 * @param {number} [m11] Horizontal scaling
	 * @param {number} [m12] Vertical skewing
	 * @param {number} [m13] Horizontal moving
	 * @param {number} [m21] Horizontal skewing
	 * @param {number} [m22] Vertical scaling
	 * @param {number} [m23] Vertical moving
	 * @param {number} [m31]
	 * @param {number} [m32]
	 * @param {number} [m33]
	 */
	constructor(m11, m12, m13, m21, m22, m23, m31, m32, m33) {
		let mat = new Array(9);
		mat[0] = m11 === undefined ? 1 : m11;
		mat[1] = m12 || 0;
		mat[2] = m13 || 0;

		mat[3] = m21 || 0;
		mat[4] = m22 === undefined ? 1 : m22;
		mat[5] = m23 || 0;

		mat[6] = m31 || 0;
		mat[7] = m32 || 0;
		mat[8] = m33 === undefined ? 1 : m33;
		this.values = mat;
	}

	/**
	 * Makes identity matrix
	 * |1, 0, 0|
	 * |0, 1, 0|
	 * |0, 0, 1|
	 *
	 * @returns {Mat3}
	 */
	identity() {
		let mat = this.values;
		mat[0] = 1;
		mat[1] = 0;
		mat[2] = 0;

		mat[3] = 0;
		mat[4] = 1;
		mat[5] = 0;

		mat[6] = 0;
		mat[7] = 0;
		mat[8] = 1;
		return this;
	}

	/**
	 * Multiplies the specified matrix with current one
	 *
	 * @param {Mat3} mat3
	 * @param {number} [px] Transform point X position
	 * @param {number} [py] Transform point Y position
	 * @returns {Mat3}
	 */
	multiply(mat3, px, py) {
		let src = this.values;
		src[2] += px || 0;
		src[5] += py || 0;
		multiply(src, mat3.values);
		src[2] -= px || 0;
		src[5] -= py || 0;
		return this;
	}

	/**
	 * Translates matrix
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {Mat3}
	 */
	translate(x, y) {
		let mat = this.values;
		mat[2] += x || 0;
		mat[5] += y || 0;
		return this;
	}

	/**
	 * Scales matrix
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {Mat3}
	 */
	scale(x, y) {
		multiply(this.values, [
			x || 1, 0, 0,
			0, y || 1, 0,
			0, 0, 1
		]);
		return this;
	}

	/**
	 * Skews matrix
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {Mat3}
	 */
	skew(x = 0, y = 0) {
		multiply(this.values, [
			1, x, 0,
			y, 1, 0,
			0, 0, 1
		]);
		return this;
	}

	/**
	 * Rotates matrix
	 *
	 * @param {number} angle Rotation angle in degrees
	 * @returns {Mat3}
	 */
	rotate(angle) {
		let rad = (angle || 0) * CONST_DEG_2_RAD_COEF,
			cos = Math.cos(rad),
			sin = Math.sin(rad);
		multiply(this.values, [
			cos, -sin, 0,
			sin, cos, 0,
			0, 0, 1
		]);
		return this;
	}

	/**
	 * Rotates matrix by X axis
	 *
	 * @param {number} angle Rotation angle in degrees
	 * @returns {Mat3}
	 */
	rotateX(angle) {
		let rad = (angle || 0) * CONST_DEG_2_RAD_COEF,
			cos = Math.cos(rad),
			sin = Math.sin(rad);
		multiply(this.values, [
			1, 0, 0,
			0, cos, -sin,
			0, sin, cos
		]);
		return this;
	}

	/**
	 * Rotates matrix by Y axis
	 *
	 * @param {number} angle Rotation angle in degrees
	 * @returns {Mat3}
	 */
	rotateY(angle) {
		let rad = (angle || 0) * CONST_DEG_2_RAD_COEF,
			cos = Math.cos(rad),
			sin = Math.sin(rad);
		multiply(this.values, [
			cos, 0, sin,
			0, 1, 0,
			-sin, 0, cos
		]);
		return this;
	}

	/**
	 * Transposes the matrix
	 *
	 * @returns {Mat3}
	 */
	transpose() {
		let mat = this.values,
			a01 = mat[1], a02 = mat[2],
			a12 = mat[5];

		mat[1] = mat[3];
		mat[2] = mat[6];

		mat[3] = a01;
		mat[5] = mat[7];

		mat[6] = a02;
		mat[7] = a12;
		return this;
	}

	/**
	 * Copy values from another matrix 3x3
	 *
	 * @param {Mat3} mat3
	 * @returns {Mat3}
	 */
	copy(mat3) {
		let a = this.values, b = mat3.values;
		a[0] = b[0];
		a[1] = b[1];
		a[2] = b[2];
		a[3] = b[3];
		a[4] = b[4];
		a[5] = b[5];
		a[6] = b[6];
		a[7] = b[7];
		a[8] = b[8];
		return this;
	}

	/**
	 * Returns clone of the matrix
	 *
	 * @returns {Mat3}
	 */
	clone() {
		let arr = this.values;
		return new Mat3(
			arr[0], arr[1], arr[2],
			arr[3], arr[4], arr[5],
			arr[6], arr[7], arr[8]
		);
	}
}